import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { Ico } from "../../../components/icons/icons.jsx";
import { FloatingPanel, useDismissOnOutsideClick } from "../../../components/ui/FloatingPanel.jsx";
import { simSearchHaystack } from "../simUtils.js";

export function SimSelectField({ label = "SIM", sims, value, onChange, error, activeOnly = true }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hi, setHi] = useState(0);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const baseId = useId();

  const options = useMemo(
    () => (activeOnly ? sims.filter((sim) => sim.status === "active") : sims),
    [sims, activeOnly],
  );

  const selected = useMemo(
    () => options.find((sim) => sim.id === value) ?? sims.find((sim) => sim.id === value) ?? null,
    [options, sims, value],
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((sim) => simSearchHaystack(sim).includes(q));
  }, [options, query]);

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  useDismissOnOutsideClick(open, close, [anchorRef, panelRef]);

  useEffect(() => setHi(0), [open, query, matches.length]);
  useEffect(() => { if (open) searchRef.current?.focus(); }, [open]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${hi}"]`)?.scrollIntoView({ block: "nearest" });
  }, [hi]);

  const select = (id) => {
    onChange(id);
    close();
  };

  const panelShadow = dark ? "0 16px 44px rgba(0,0,0,.55)" : "0 16px 40px rgba(15,23,42,.14)";

  return (
    <label style={{ display: "block", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: dark ? `${A}18` : `${A}10`, border: `1px solid ${dark ? `${A}35` : `${A}25`}`, color: A, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 22 }}>
          {Ico.sim}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 8 }}>{label}</span>
          <button
            ref={anchorRef}
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-controls={`${baseId}-list`}
            aria-haspopup="listbox"
            aria-label={label}
            onClick={() => setOpen((v) => !v)}
            style={{
              width: "100%",
              minHeight: 42,
              padding: "8px 12px",
              borderRadius: 8,
              border: `1px solid ${error ? "#EF4444" : c.cardBdr}`,
              background: dark ? "rgba(255,255,255,0.04)" : c.card,
              color: c.text,
              fontSize: 13,
              textAlign: "left",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              boxSizing: "border-box",
            }}
          >
            <span style={{ minWidth: 0 }}>
              {selected ? (
                <>
                  <span style={{ display: "block", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selected.iccid}</span>
                  <span style={{ display: "block", marginTop: 2, fontSize: 11, color: c.sub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selected.carrier} · {selected.simType}</span>
                </>
              ) : (
                <span style={{ color: c.sub }}>Select SIM…</span>
              )}
            </span>
            <ChevronIcon open={open} />
          </button>

          <FloatingPanel anchorRef={anchorRef} panelRef={panelRef} open={open} gap={6}>
            <div style={{ background: c.card, border: `1px solid ${c.cardBdr}`, borderRadius: 10, boxShadow: panelShadow, overflow: "hidden" }}>
              <div style={{ padding: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 8, border: `1px solid ${c.cardBdr}`, background: c.hover }}>
                  <span style={{ color: c.sub, display: "flex", flexShrink: 0 }}>{Ico.search}</span>
                  <input
                    ref={searchRef}
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => handleSearchKeys(e, hi, matches, select, setHi, close)}
                    placeholder="Search ICCID, MSISDN, carrier…"
                    aria-label="Search SIM cards"
                    style={{ width: "100%", border: "none", outline: "none", background: "transparent", fontSize: 12.5, color: c.text, fontFamily: "inherit" }}
                  />
                </div>
              </div>
              <ul id={`${baseId}-list`} ref={listRef} role="listbox" style={{ listStyle: "none", margin: 0, padding: "0 6px 8px", maxHeight: 220, overflow: "auto" }}>
                {matches.length === 0 ? (
                  <li style={{ padding: "16px 12px", textAlign: "center", fontSize: 12, color: c.sub }}>No SIM cards found</li>
                ) : (
                  matches.map((sim, idx) => (
                    <li key={sim.id} role="option" aria-selected={sim.id === value} data-idx={idx} style={{ marginBottom: 2 }}>
                      <button
                        type="button"
                        onMouseEnter={() => setHi(idx)}
                        onClick={() => select(sim.id)}
                        style={{
                          width: "100%",
                          display: "block",
                          textAlign: "left",
                          padding: "8px 10px",
                          border: "none",
                          borderRadius: 8,
                          background: sim.id === value ? (dark ? `${A}18` : `${A}0c`) : idx === hi ? c.hover : "transparent",
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", color: sim.id === value ? A : c.text }}>{sim.iccid}</div>
                        <div style={{ fontSize: 11, color: c.sub, marginTop: 2 }}>{sim.carrier} · {sim.simType} · {sim.msisdn}</div>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </FloatingPanel>

          {error ? <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "#EF4444", fontWeight: 600 }}>{error}</span> : null}
        </div>
      </div>
    </label>
  );
}

function handleSearchKeys(e, hi, matches, select, setHi, close) {
  if (e.key === "ArrowDown") {
    e.preventDefault();
    setHi((i) => Math.min(i + 1, matches.length - 1));
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    setHi((i) => Math.max(i - 1, 0));
  } else if (e.key === "Enter") {
    e.preventDefault();
    if (matches[hi]) select(matches[hi].id);
  } else if (e.key === "Escape") {
    e.preventDefault();
    close();
  }
}

function ChevronIcon({ open }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s ease" }}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
